/**
 * Os anexos do catálogo na emissão do PCMSO e do PGR (#520). A lista chega por rota da própria
 * emissão — quem emite não precisa ver o cadastro para escolher — e vem marcada como na última
 * emissão desta empresa para este documento. Sem anexo ativo, o grupo não aparece e o documento
 * sai sem anexos: não é pré-requisito da emissão.
 *
 * Os anexos montados pelo sistema com os dados da empresa (#551) entram na mesma lista, na ordem
 * do número, e vêm marcados salvo o que a última emissão deixou de fora. Para eles a tela guarda
 * o contrário: as chaves desmarcadas, porque ausente quer dizer que sai.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, FormGroup, Paper, Typography } from '@mui/material';
import documentService from '../../../services/documentService';
import type { ReportType } from '../../../types/document';
import {
  aceitaAnexo,
  intercalarAnexos,
  rotuloDoAnexo,
  type AnnexBlockOption,
  type AttachmentOption,
} from '../../../types/documentAttachment';

interface AttachmentSelectorProps {
  companyId: number | null;
  type: ReportType | '';
  /** Ids marcados, na ordem do catálogo. */
  value: number[];
  onChange: (ids: number[]) => void;
  /** Chaves dos anexos montados que ficam de fora. */
  omittedAnnexBlocks: string[];
  onOmittedAnnexBlocksChange: (keys: string[]) => void;
}

/** O que foi carregado para uma combinação empresa + documento. */
interface Carga {
  chave: string;
  opcoes: AttachmentOption[];
  montados: AnnexBlockOption[];
  lembrou: boolean;
  erro: string | null;
}

function rotuloDaOpcao(opcao: AttachmentOption): string {
  return opcao.normReference ? `${rotuloDoAnexo(opcao)} · ${opcao.normReference}` : rotuloDoAnexo(opcao);
}

const AJUDA_DO_MONTADO = 'montado com os dados da empresa na data da emissão';

export const AttachmentSelector: React.FC<AttachmentSelectorProps> = ({
  companyId,
  type,
  value,
  onChange,
  omittedAnnexBlocks,
  onOmittedAnnexBlocksChange,
}) => {
  const [carga, setCarga] = useState<Carga | null>(null);

  /*
   * O efeito lê o `onChange` pela referência: sem isso, um pai que passe uma função nova a
   * cada render recarregaria a lista a cada marcação.
   */
  const onChangeRef = useRef(onChange);
  const onOmittedRef = useRef(onOmittedAnnexBlocksChange);
  useEffect(() => {
    onChangeRef.current = onChange;
    onOmittedRef.current = onOmittedAnnexBlocksChange;
  }, [onChange, onOmittedAnnexBlocksChange]);

  const chave = companyId !== null && aceitaAnexo(type) ? `${companyId}:${type}` : null;

  /*
   * Trocar de empresa ou de documento recarrega a lista e a marcação. A marcação anterior sai
   * antes da carga: um id de outro documento que chegasse à prévia ou à emissão seria recusado.
   */
  useEffect(() => {
    onChangeRef.current([]);
    onOmittedRef.current([]);
    if (companyId === null || !aceitaAnexo(type)) return;

    const chaveDaCarga = `${companyId}:${type}`;
    let ativo = true;
    documentService
      .listAttachmentOptions(companyId, type)
      .then((resultado) => {
        if (!ativo) return;
        // O id chega do banco como texto: a tela e o pedido trabalham com número.
        const opcoes = resultado.attachments.map((opcao) => ({ ...opcao, id: Number(opcao.id) }));
        const disponiveis = new Set(opcoes.map((opcao) => opcao.id));
        const lembrados = new Set(resultado.lastSelection.map(Number).filter((id) => disponiveis.has(id)));
        const montados = resultado.annexBlocks ?? [];
        const omitidosAntes = new Set(resultado.lastOmittedAnnexBlocks ?? []);
        const omitidos = montados.filter((bloco) => omitidosAntes.has(bloco.key)).map((bloco) => bloco.key);
        setCarga({
          chave: chaveDaCarga,
          opcoes,
          montados,
          lembrou: lembrados.size > 0 || omitidos.length > 0,
          erro: null,
        });
        onChangeRef.current(opcoes.filter((opcao) => lembrados.has(opcao.id)).map((opcao) => opcao.id));
        onOmittedRef.current(omitidos);
      })
      .catch(() => {
        if (!ativo) return;
        setCarga({
          chave: chaveDaCarga,
          opcoes: [],
          montados: [],
          lembrou: false,
          erro: 'Não foi possível carregar os anexos. O documento pode ser emitido sem eles.',
        });
        onChangeRef.current([]);
        onOmittedRef.current([]);
      });

    return () => {
      ativo = false;
    };
  }, [companyId, type]);

  // A carga de outra empresa ou documento não vale para a escolha atual.
  const atual = carga !== null && carga.chave === chave ? carga : null;
  if (atual === null) return null;

  if (atual.erro) {
    return <Alert severity="warning">{atual.erro}</Alert>;
  }

  if (atual.opcoes.length === 0 && atual.montados.length === 0) return null;

  const alternar = (id: number): void => {
    const marcados = new Set(value);
    if (marcados.has(id)) marcados.delete(id);
    else marcados.add(id);
    onChange(atual.opcoes.filter((opcao) => marcados.has(opcao.id)).map((opcao) => opcao.id));
  };

  const alternarMontado = (key: string): void => {
    const omitidos = new Set(omittedAnnexBlocks);
    if (omitidos.has(key)) omitidos.delete(key);
    else omitidos.add(key);
    onOmittedAnnexBlocksChange(atual.montados.filter((bloco) => omitidos.has(bloco.key)).map((bloco) => bloco.key));
  };

  const lista = intercalarAnexos(atual.opcoes, atual.montados);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Anexos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {atual.lembrou
          ? 'Marcados como na última emissão desta empresa'
          : 'Os marcados saem no fim do documento, cada um em página própria.'}
      </Typography>
      <FormGroup>
        {lista.map((item) =>
          'key' in item ? (
            <Box key={`montado:${item.key}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!omittedAnnexBlocks.includes(item.key)}
                    onChange={() => alternarMontado(item.key)}
                    slotProps={{ input: { 'aria-describedby': `anexo-montado-${item.key}` } }}
                  />
                }
                label={rotuloDoAnexo(item)}
              />
              <Typography
                id={`anexo-montado-${item.key}`}
                variant="body2"
                color="text.secondary"
                sx={{ ml: 4, mt: -0.75 }}
              >
                {AJUDA_DO_MONTADO}
              </Typography>
            </Box>
          ) : (
            <FormControlLabel
              key={item.id}
              control={<Checkbox checked={value.includes(item.id)} onChange={() => alternar(item.id)} />}
              label={rotuloDaOpcao(item)}
            />
          )
        )}
      </FormGroup>
    </Paper>
  );
};

export default AttachmentSelector;
